import { useEffect, useRef, type RefObject } from "react";
import { scriptScrollAnchorKey, type ScriptScrollAnchor } from "@/services/scriptScrollAnchor";
import { scrollToTextAnchor } from "@/utils/domTextSearch";

const SCROLL_SETTLE_DELAY_MS = 200;

export interface UseScriptAutoScrollArgs {
  readonly containerRef: RefObject<HTMLElement | null>;
  readonly anchor: ScriptScrollAnchor | null;
  readonly enabled: boolean;
  readonly contentReady: boolean;
}

export function useScriptAutoScroll({ containerRef, anchor, enabled, contentReady }: UseScriptAutoScrollArgs): void {
  const appliedKeyRef = useRef<string | null>(null);
  const anchorKey = scriptScrollAnchorKey(anchor);

  useEffect(() => {
    if (!enabled) {
      appliedKeyRef.current = null;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !contentReady || anchor === null || appliedKeyRef.current === anchorKey) {
      return;
    }

    const timer = window.setTimeout(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const scrolled = scrollToTextAnchor({
        container,
        startAnchorText: anchor.startAnchorText,
        endAnchorText: anchor.endAnchorText,
        fraction: anchor.shotFraction
      });
      if (scrolled) {
        appliedKeyRef.current = anchorKey;
      }
    }, SCROLL_SETTLE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [enabled, contentReady, anchorKey, containerRef]);
}
